import { HttpClient } from 'core/common/services';
import { convertToBase64 } from 'core/common/utils/convertToBase64';
import { convertToBlob } from 'core/common/utils/convertToBlob';
import { stripBase64Prefix } from 'core/common/utils/stripBase64Prefix';
import { PhotoScan } from '../../entities';
import { DetectionService } from '../../interfaces';
import { FaceDtoMapper } from '../../mappers';
import { FacePhotoDTO } from '../dto';

type ErrorResponse = {
  error: { message: string; name: string; display: string; type: string };
  result: boolean;
};

export class FaceDetectionService implements DetectionService {
  constructor(private readonly httpClient: HttpClient) {}

  async detect(image: HTMLCanvasElement): Promise<PhotoScan> {
    const dtoMapper = new FaceDtoMapper();

    const scan = new FormData();
    const imageBlob = await convertToBlob(image);

    if (imageBlob) {
      scan.append('photo', imageBlob, 'image.png');
    }

    const { data } = await this.httpClient.post<FacePhotoDTO | ErrorResponse>(
      '/readings/face/detect',
      {
        data: scan,
        headers: {
          'Content-type': 'multipart/form-data',
        },
      },
    );

    if ('error' in data) {
      const result = await this.detectBase64(image);
      return result;
    }
    return dtoMapper.toEntity(data);
  }

  async detectBase64(image: HTMLCanvasElement): Promise<PhotoScan> {
    const dtoMapper = new FaceDtoMapper();

    const fileString = convertToBase64(image);

    const photo = stripBase64Prefix(fileString);

    const { data } = await this.httpClient.post<FacePhotoDTO | ErrorResponse>(
      '/readings/face/detect-base64',
      {
        data: {
          photo,
        },
      },
    );

    if ('error' in data) {
      throw Error('Error occured detecting face scan');
    }

    return dtoMapper.toEntity(data);
  }

  async uploadPhoto(file: File): Promise<PhotoScan> {
    const dtoMapper = new FaceDtoMapper();

    const scan = new FormData();
    scan.append('photo', file, 'image.png');

    const { data } = await this.httpClient.post<FacePhotoDTO | ErrorResponse>(
      '/readings/face/detect',
      {
        data: scan,
        headers: {
          'Content-type': 'multipart/form-data',
        },
      },
    );

    if ('error' in data) {
      throw Error('Error occured uploading face scan');
    }

    return dtoMapper.toEntity(data);
  }
}
